/** @module csi/widget/SplitPane */
define([
	"dojo/_base/declare",
	"dojo/on",
	"dojo/touch",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"csi/html",
	"dojo/text!./SplitPane.html"
], function(declare,on,touch,_TemplatedMixin,_WidgetBase,html,template) {

	/**
	 * @typedef Object SplitPaneDimensions
	 * @property  {number} full - total width of splitter
	 * @property  {number} pane1 - width of first pane
	 * @property  {number} pane2 - width of second pane
	 * @property  {number} split - split percentage
	 * @property  {number} splitter - width of splitter
	 */
	
	/**
	 * Calculate split percentage based on size of pane in pixels, the full size of the container, and the splitter size
	 * @param {number} pixels
	 * @param {number} full
	 * @param {number} splitter
	 * @return {number}
	 */
	function _calculateSplit(pixels, full, splitter) {
		if (pixels === 0) {
			return 0;
		} else if (pixels === full) {
			return 1;
		}
		
		return (pixels + (splitter / 2.0)) / full;
	}

	return declare([_WidgetBase,_TemplatedMixin], {
		baseClass: "csi-split-pane",
		templateString: template,
		pane1: null,
		pane2: null,
		splitter: null,

		/** @property {boolean} allowSnapToZero - if true, then snap split value to 0 when first pane's width is less than minPixels1 */
		allowSnapToZero: true,

		/** @property {DOMNode|string} content1 - HTML content to be placed in pane1 */
		content1: null,

		/** @property {DOMNode|string} content2 - HTML content to be placed in pane2 */
		content2: null,

		/** @property {boolean} frozen - by default the splitter is not draggable; do NOT use "disabled" property here as that will affect widgets within the panes */
		frozen: true,

		/** @property {number} hideAtZero - set to true to allow left or right pane to be hidden when splitter at either extreme (0 or 1), even if minPixels# is non-zero; must be frozen to take effect */
		hideAtZero: false,

		/** @property {number} maxPixels1 - maximum number of pixels allowed in pane1 (otherwise can no longer drag wider) */
		maxPixels1: null,

		/** @property {number} maxPixels2 - maximum number of pixels allowed in pane2 (otherwise can no longer drag wider) */
		maxPixels2: null,

		/** @property {number} minPixels1 - minimum number of pixels allowed in pane1 (otherwise will be hidden) */
		minPixels1: 50,

		/** @property {number} minPixels2 - minimum number of pixels allowed in pane2 (otherwise will be hidden) */
		minPixels2: 50,

		/** @property {number} pixelSize1 - if non-zero, then indicates a fixed pixel size of pane1 (do not use in conjunction with pixelSize2) */
		pixelSize1: 0,
		
		/** @property {number} pixelSize2 - if non-zero, then indicates a fixed pixel size of pane2 (do not use in conjunction with pixelSize1) */
		pixelSize2: 0,
		
		/** @property {number} split - percentage allocated to each pane */
		split: 0.5,

		/** @property {boolean} vertical - indicates if panes are oriented vertically (true) or horizontally (false, default) */
		vertical: false,

		/** @property {boolean} warning - set to true if splitter should be styled to indicate a warning */
		warning: false,

		/** @property {number} zoom - current zoom level - necessary to correctly drag splitter */
		zoom: 1,

		/** @property {boolean} _dragging - indicates user is currently dragging the splitter */
		_dragging: false,

		/** @property {number} _dragStartSplit - split value when start drag splitter, to detect change in split on release */
		_dragStartSplit: null,

		/**
		 * Return split percentage that would be needed to make the size of pane1 or pane2 the number of pixels given
		 * @param {number} pixels
		 * @param {boolean} forPane2 - set to true to find split percentage if number of pixels if for pane 2 
		 * @return {number}
		 */
		getSplitForPixels: function(pixels, forPane2) {
			var full = this.vertical ? $(this.domNode).height() : $(this.domNode).width();
			var splitter = this.vertical ? $(this.splitter).height() : $(this.splitter).width();
			var split = _calculateSplit(pixels, full, splitter);
			return forPane2 ? (1.0 - split) : split;
		},
		
		/**
		 * Extension point to determine if hiding one of split panes is valid
		 * @param {boolean} hidden1 - true if first split pane is hidden
		 * @param {boolean} hidden2 - true if second split pane is hidden
		 * @return {boolean}
		 */
		isValid: function(hidden1, hidden2) {
			return true;
		},

		/**
		 * Extension point for when split percentage changes
		 * @param {number} value
		 * @param {boolean} dragging - true if still dragging mouse
		 */
		onChangeSplit: function(value, dragging) { },

		/**
		 * Extension point for when splitter is clicked
		 */
		onStartDrag: function() { },

		/**
		 * Update DOM based on new properties
		 */
		resize: function() {
			var dim = this.getDimensions();
			if (!dim) {
				return;
			}

			var attr = this.vertical ? html.HEIGHT : html.WIDTH;

			$(this.domNode).toggleClass(this.baseClass + "-min", !dim.pane1);
			$(this.domNode).toggleClass(this.baseClass + "-max", !dim.pane2);
			$(this.pane1).css(attr, (100 * dim.pane1 / dim.full) + "%");
			$(this.pane2).css(attr, (100 * dim.pane2 / dim.full) + "%");

			// update splitter warning if at the extremes - don't want to accidentally hide anything without a warning
			this.set("warning", (dim.pane1 && dim.pane2) ? false : !this.isValid(!dim.pane1, !dim.pane2));
			
			// resize child widgets
			html.resizeChildren(this.pane1);
			html.resizeChildren(this.pane2);
		},

		/**
		 * Finish initializing DOM on startup
		 */
		startup: function() {
			this.inherited(arguments);
			this.resize();
		},

		/**
		 * Determine if content is available from source node. There can be 2 children, the first that fills
		 * pane1, and the other for pane2.
		 */
		_fillContent: function() {
			if (!this.srcNodeRef) {
				return;
			}

			var children = this.srcNodeRef.children;
			var child1 = children[0];
			var child2 = children[1];

			console.assert(!children[2], "csi/widget/SplitPane can have at most 2 children");

			if (child1) {
				this.pane1.appendChild(child1);
			}

			if (child2) {
				this.pane2.appendChild(child2);
			}
		},

		/**
		 * Return object with pixels sizes for widget, first pane, splitter, and second pane
		 * @param {number} - [newSplit] - optional new value for split, on move
		 * @return {SplitPaneDimensionst|null} - returns null if widget currently has no size
		 */
		getDimensions: function(newSplit) {
			if (!this.domNode.parentNode) {
				return null;
			}

			var full = this.vertical ? $(this.domNode).height() : $(this.domNode).width();
			if (!full) {
				return null;
			}

			var splitter = this.vertical ? $(this.splitter).height() : $(this.splitter).width();
			var pane1 = 0;

			newSplit = newSplit || this.split;

			// splitter is hidden when frozen and split is at one end or the other, so do not include in calculations in such cases
			if (this.pixelSize1) {
				pane1 = this.pixelSize1;
			} else if (this.pixelSize2) {
				pane1 = full - this.pixelSize2 - splitter;
			} else if (newSplit === 1) {
				pane1 = full;
			} else if (newSplit !== 0) {
				pane1 = Math.round((full * newSplit) - (splitter / 2.0));
			}

			// if left or right panes are smaller than their minimums, snap them to zero width/height,
			// unless snapping to zero is not allowed, then snap to the minimum pane width
			const allowZero = this.allowSnapToZero || (this.frozen && this.hideAtZero);

			if (pane1 <= this.minPixels1) {
				pane1 = allowZero ? 0 : this.minPixels1;
			} else if (this.minPixels2) {
				const pane1Threshold = full - splitter - this.minPixels2;
				
				if (pane1 > pane1Threshold) {
					pane1 = allowZero ? full : pane1Threshold;
				}
			}

			// do not allow splitter to move beyond left and right pane maximums
			if (this.maxPixels1 && pane1 > this.maxPixels1) {
				pane1 = this.maxPixels1;
			} else if (this.maxPixels2) {
				pane1 = Math.max(pane1, full - splitter - this.maxPixels2);
			}

			// calculate pane2 from pane1
			var pane2 = full - pane1;
			
			if (pane1 === 0) {
				// if splitter all the way to the left, then decrease pane2 size if splitter is not frozen
				if (!this.frozen) {
					pane2 -= splitter;
				}
			} else if (pane1 === full) {
				// if splitter all the way to the right, then decrease pane1 size if splitter is not frozen
				if (!this.frozen) {
					pane1 -= splitter;
				}
			} else {
				// decrease size of pane2 by the splitter since it is somewhere in the middle
				pane2 -= splitter;
			}
			
			// determine new split percentage from pane1 value
			newSplit = _calculateSplit(pane1, full, splitter);

			return {
				pane1: pane1,
				pane2: pane2,
				splitter: splitter,
				full: full,
				split: Number(newSplit.toFixed(3))
			};
		},

		/**
		 * Handler for splitter onmouseenter / onmouseleave events
		 * @param {Event} e
		 */
		_onMouse: function(e) {
			if (!this.frozen) {
				$(this.splitter).toggleClass("over", (e.type == "mouseover" || e.type == "mouseenter"));
			}
		},

		/**
		 * Handler for mouse press on splitter to start drag
		 * @param {Event} e
		 */
		_onPress: function(e) {
			if (this.frozen) {
				return;
			}

			this.onStartDrag();

			var self = this;
			var dim1 = this.getDimensions();
			var axis = this.vertical ? "pageY" : "pageX";
			var start = e[axis];
			var splitPos = dim1.split * dim1.full;
			var factor = 1 / this.zoom;

			this._dragStartSplit = dim1.split;
			this._dragging = true;

			var handles = [
				on(document, touch.move, function(e) {
					var delta = e[axis] - start;
					var x = (splitPos + factor * delta) / dim1.full;
					var dim2 = self.getDimensions(x);
					if (dim2) {
						var newSplit = dim2.split;
						if (newSplit != self.split) {
							self.set("split", newSplit);
						}
					}
				}),
				on(document, touch.release, function() {
					var h;

					while (h = handles.pop()) {
						h.remove();
					}

					self._dragging = false;

					if (self._dragStartSplit !== self.split) {
						self.onChangeSplit(self.split, self._dragging);
					}
				})
			];

			e.stopPropagation();
			e.preventDefault();
		},

		/**
		 * Place content in pane1
		 * @param {DOMNode|string} content
		 */
		_setContent1Attr: function(content) {
			$(this.pane1).append(content);
		},

		/**
		 * Place content in pane1
		 * @param {DOMNode|string} content
		 */
		_setContent2Attr: function(content) {
			$(this.pane2).append(content);
		},

		/**
		 * Disable drag and drop of splitter
		 * @param {boolean} frozen
		 */
		_setFrozenAttr: function(frozen) {
			this._set("frozen", frozen);
			$(this.domNode).toggleClass(this.baseClass + "-frozen", frozen);
		},

		/**
		 * Set split percentage based on pixel size of pane1
		 * @param {number} value
		 */
		_setPixelSize1Attr: function(value) {
			if (this._validatePixelSize(value)) {
				const split = this.getSplitForPixels(value);
				this.set("split", split);
				this._set("pixelSize1", value);
			}
		},
		
		/**
		 * Set split percentage based on pixel size of pane2
		 * @param {number} value
		 */
		_setPixelSize2Attr: function(value) {
			if (this._validatePixelSize(value)) {
				const split = this.getSplitForPixels(value, true);
				this.set("split", split);
				this._set("pixelSize2", value);
			}
		},
		
		/**
		 * Handle change to split percentage
		 * @param {number} value
		 */
		_setSplitAttr: function(value) {
			var oldValue = this.split;
			var newValue = Math.min(Math.max(0, value), 1);

			this._set("pixelSize1", 0);
			this._set("pixelSize2", 0);
			this._set("split", newValue);
			this.resize();

			// notify of change if the split was set explicitly (whether the value changed or not), or if value changed while dragging
			if (!this._dragging || (newValue != oldValue)) {
				this.onChangeSplit(newValue, this._dragging);
			}
		},

		/**
		 * Handle change to orientation of panes
		 * @param {number} value
		 */
		_setVerticalAttr: function(value) {
			this._set("vertical", value);

			// reset previous setting for split before updating
			var attr = value ? html.WIDTH : html.HEIGHT;
			$(this.pane1).css(attr, "");
			$(this.pane2).css(attr, "");

			// update DOM based on new setting
			$(this.domNode).toggleClass(this.baseClass + "-vertical", value);
			this.resize();
		},

		/**
		 * Toggle warning class on splitter when changed
		 */
		_setWarningAttr: {node: "splitter", type: "toggleClass"},
		
		/**
		 * Check that given pixel size is valid for split pane
		 * @param {number} pixels
		 * @return {boolean}
		 */
		_validatePixelSize: function(pixels) {
			if (pixels <= 0) {
				console.assert(false, "SplitPane: must use positive value for pixelSize1/2");
				return false;
			}
			
			if (!this.frozen) {
				console.assert(false, "SplitPane: does not make sense to set pixelSize1/2 when splitter is not frozen");
				return false;
			}
			
			return true;
		}
	});
});
